import FormFields from '@modules/design-system/types/FormFields.entity';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import type Feature from '@modules/store/common/types/Feature.entity';
import type FeaturesState from '@modules/store/common/types/FeaturesState.entity';
import { snakeCase } from 'change-case';
import { get } from 'lodash';
import { GetterTree } from 'vuex';

const togglesPrefix = 'toggles.';

const enum ToggleValue {
  TRUE = 'true',
  FALSE = 'false',
}

export const getters: GetterTree<FeaturesState, CommonState> = {
  loading(state) {
    return state.loading;
  },
  getFeature(state): Feature {
    return (featurePathName: string): any => {
      const allowLocalStorageSelector: boolean = featurePathName.startsWith(togglesPrefix);
      if (window && window.localStorage && allowLocalStorageSelector) {
        // format ___toggle_{snake case toggle name as found in the features 'toggles` section

        const path = `___toggle_${snakeCase(featurePathName.substring(togglesPrefix.length))}`;
        const toggleName: null | string = window.localStorage.getItem(path);

        switch (toggleName) {
          case ToggleValue.TRUE:
            return true;
          case ToggleValue.FALSE:
            return false;
        }
      }

      return get(state.features, featurePathName);
    };
  },
  getFeatureFormFields(state): (featurePathName: string, rules: string) => FormFields[] {
    return (featurePathName: string, rules: string): FormFields[] => {
      const featureData: any = get(state.features, featurePathName);
      const fields: any = featureData.fields;
      const terms: any = featureData.terms;
      const ids: any = featureData.ids;
      const columns: any = featureData.columns;
      const components: any = featureData.components;
      const attributes: any = featureData.attributes;
      const options: any = featureData.options;
      const masks: any = featureData.masks;
      const custom: any = featureData.custom;
      const defaults: any = featureData.defaults;
      const refs: any = featureData.refs;
      const helpTexts: any = featureData.helpTexts;
      const meta: any = featureData.meta;
      const props: any = featureData.props;

      return fields.map((field: any) => {
        return {
          name: field,
          term: terms[field],
          id: ids ? ids[field] : null,
          component: components[field],
          mask: masks ? masks[field] : '',
          custom: custom ? custom[field] : null,
          default: defaults ? defaults[field] : null,
          helpText: {
            titlePath: helpTexts ? helpTexts[field]?.titlePath : null,
            textPath: helpTexts ? helpTexts[field]?.textPath : null,
            dataCy: helpTexts ? helpTexts[field]?.dataCy : null,
          },
          ref: refs ? refs[field] : null,
          columns: columns[field],
          rules: rules[field],
          attributes: attributes ? attributes[field] : [],
          options: options ? options[field] : [],
          meta,
          props: props ? props[field] : {},
        } as FormFields;
      });
    };
  },
};
